// A station's live-ops (spec/13 §3.3; F-STN-17, F-STN-18, F-STN-20, F-SES-15, F-SES-24), shared by the two
// places that offer them: the station modal's action bar and the floor tile's quick-actions menu. One set of
// permission gates (`stationActionGates`), one set of forms and confirms, so an action reads and behaves the
// same wherever it is started.
//
// Maintenance and lock are reversible, so they happen at once and offer Undo. Restart and shut down interrupt a
// player, so they confirm and state the consequence first (spec/13 §2). A command's toast says it was sent —
// "the station runs it when it reconnects" is the honest word for an offline station.

import type { FloorStationState, OfflineCode, Session, StationPowerAction } from '@clavian/api-client';
import { OFFLINE_CODE_ACTIONS, OFFLINE_CODE_MINUTE_BUCKETS, type OfflineCodeAction } from '@clavian/contracts/constants';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, Checkbox, SegmentedControl, SelectField, TextField, useToast } from '@clavian/ui-web';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';

import type { Access } from '../api/queries';
import { PROBLEM, problemCode } from '../api/problems';
import { Icon } from '../shell/nav-icons';
import { useCurrentVenue } from '../shell/scope';
import { Notice, Section } from '../ui';
import { useGenerateOfflineCode, useLockStation, useMessageStation, useSetStationMaintenance, useSkipStationSetup, useStationPower } from './queries';
import { useStaffMessageRead } from './realtime';
import { useLockSession, useMessageSession } from '../org/sessions/queries';
import { useProblem } from '../api/use-problem';

/** The longest line a station toast carries (matches the contract's `StationMessageRequest.text`). */
const MESSAGE_MAX = 280;

const MAINTENANCE_DURATIONS = ['30', '60', '120', '240'] as const;

/** What a role may do to one station now. Sell time, Add time and Lock need a running session it can read. */
export interface StationActionGates {
  readonly sell: boolean;
  readonly comp: boolean;
  readonly lock: boolean;
  readonly message: boolean;
  readonly maintenance: boolean;
  readonly power: boolean;
  readonly recovery: boolean;
  readonly offlineCode: boolean;
}

/**
 * The gates, from the role's permissions and the station's running session (null when there is none, or the role
 * cannot read sessions) — the same permissions the sheet's sections and the session panel always checked.
 */
export function stationActionGates(access: Access, running: Session | null, station?: Pick<FloorStationState, 'managed' | 'client'>): StationActionGates {
  const can = (permission: string) => access.status === 'ready' && access.can(permission);
  // A station staff run (ADR-0033) has no app: nothing to message, put in maintenance, power, or unlock with a code.
  // Nor has a PC with no Clavian Station app (`client: none`, ADR-0065): it starts from the boot server and nothing more.
  const hasApp = station?.managed !== false && station?.client !== 'none';
  return {
    sell: running !== null && can('orders:create'),
    // A booked console session (ADR-0034) is paid once at the end: no comp funds it — the order is discounted instead.
    comp: running !== null && running.booking === undefined && can('sessions:comp'),
    lock: running !== null && can('sessions:lock'),
    message: hasApp && can('stations:message'),
    maintenance: hasApp && can('stations:maintenance'),
    power: hasApp && can('stations:power'),
    recovery: hasApp && can('stations:recovery'),
    offlineCode: hasApp && can('sessions:offline_code'),
  };
}

/** A running session: one that has not ended. */
export function runningSession(session: Session | null | undefined): Session | null {
  return session !== null && session !== undefined && session.status !== 'ended' ? session : null;
}

export function useCommandToasts() {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  return (action: string, undo?: () => void) => ({
    onSuccess: () =>
      show({
        tone: 'ok' as const,
        title: t('floor.drawer.sent', { action }),
        ...(undo === undefined ? {} : { action: { label: t('common.undo'), onAction: undo } }),
      }),
    onError: (error: unknown) =>
      show({
        tone: 'danger' as const,
        title: problem.text(error, { fallback: t('floor.drawer.failed', { action }) }),
      }),
  });
}

/** Lock or unlock a session's station (F-SES-15), issued through the gateway; reversible, so it offers Undo. */
export function useSessionLock() {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const venue = useCurrentVenue();
  const lock = useLockStation(venue.organizationId, venue.id);
  const staffLock = useLockSession(venue.organizationId, venue.id);
  /** `session` is the running session: a staff-run one (ADR-0033) is locked on the server itself, there is no app. */
  const send = (stationId: string, next: boolean, undoable: boolean, session?: Session): void => {
    if (session?.staffRun === true) {
      staffLock.mutate(
        { sessionId: session.id, stationId, locked: next },
        {
          onSuccess: (updated) =>
            show({
              tone: 'ok',
              title: next ? t('floor.drawer.staffRun.lockedToast') : t('floor.drawer.staffRun.unlockedToast'),
              ...(undoable ? { action: { label: t('common.undo'), onAction: () => send(stationId, !next, false, updated) } } : {}),
            }),
          onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('floor.drawer.staffRun.lockFailed') }) }),
        },
      );
      return;
    }
    lock.mutate(
      { stationId, body: { locked: next } },
      {
        onSuccess: () =>
          show({
            tone: 'ok',
            title: next ? t('sessions.actions.lockSent') : t('sessions.actions.unlockSent'),
            ...(undoable ? { action: { label: t('common.undo'), onAction: () => send(stationId, !next, false) } } : {}),
          }),
        onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('sessions.actions.lockFailed') }) }),
      },
    );
  };
  return { send, pending: lock.isPending || staffLock.isPending };
}

/** Maintenance mode (F-STN-18): start for a while, or end — each with Undo. */
export function useMaintenance(station: FloorStationState) {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const maintenance = useSetStationMaintenance(venue.organizationId, venue.id);
  const notify = useCommandToasts();
  const end = (undoable: boolean, restartMinutes = 30): void =>
    maintenance.mutate(
      { stationId: station.stationId, body: { enabled: false } },
      notify(t('floor.drawer.endMaintenance'), undoable ? () => start(restartMinutes, false) : undefined),
    );
  const start = (minutes: number, undoable: boolean, onSuccess?: () => void): void => {
    const toasts = notify(t('floor.drawer.startMaintenance'), undoable ? () => end(false, minutes) : undefined);
    maintenance.mutate(
      { stationId: station.stationId, body: { enabled: true, durationMinutes: minutes } },
      {
        ...toasts,
        onSuccess: () => {
          toasts.onSuccess();
          onSuccess?.();
        },
      },
    );
  };
  return { start, end, pending: maintenance.isPending, inMaintenance: station.online && station.mode === 'maintenance' };
}

/** Sleep and wake go at once — they need no confirm; restart and shut down confirm first (`PowerConfirm`). */
export function usePowerNow(station: FloorStationState) {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const power = useStationPower(venue.organizationId, venue.id);
  const notify = useCommandToasts();
  return {
    send: (action: StationPowerAction) => power.mutate({ stationId: station.stationId, body: { action } }, notify(t(`floor.power.${action}`))),
    pending: power.isPending,
  };
}

/**
 * A form's frame: a titled section in the modal (with `actions` — its close button — in the header), or `plain`
 * inside a small dialog that already carries the title.
 */
function Frame({
  plain,
  title,
  description,
  actions,
  children,
}: {
  readonly plain: boolean;
  readonly title: string;
  readonly description?: string;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
}): ReactNode {
  if (plain) {
    return (
      <div className="console-station-action">
        {description === undefined ? null : <p className="console-muted">{description}</p>}
        {children}
      </div>
    );
  }
  return (
    <Section title={title} description={description} titleAs="h3" actions={actions}>
      {children}
    </Section>
  );
}

interface ActionFormProps {
  readonly station: FloorStationState;
  /** Inside a dialog that carries the title: no section of its own. */
  readonly plain?: boolean;
  /** In the section's header: the close button. */
  readonly actions?: ReactNode;
}

/**
 * Message a player at the station (F-STN-20, F-SES-20): a short line shown as a kiosk toast and in-game overlay,
 * optionally needing acknowledgement. While a session runs on the station (`session`), it goes to the **session**
 * (`POST …/sessions/{id}/message`), so it is recorded on the session's timeline as the Sessions screen's Message was;
 * on a station with none, it goes to the station (`POST …/stations/{s}/message`), which needs no session, and a
 * message that needs acknowledgement is followed until the player gives it — the station command's id is what that
 * follows, and only the station's route returns it. Both ride the same command channel as lock and power (spec/09 §5.1).
 */
export function MessageControls({ station, session = null, plain = false, actions }: ActionFormProps & { readonly session?: Session | null }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const venue = useCurrentVenue();
  const message = useMessageStation(venue.organizationId, venue.id);
  const toSession = useMessageSession(venue.organizationId, session?.id ?? '');
  const [text, setText] = useState('');
  const [needsAck, setNeedsAck] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  // The last message sent here that needs acknowledgement, followed until the player acknowledges it (F-STN-20).
  const [awaiting, setAwaiting] = useState<string | null>(null);
  // The last message went to the session: it is on the session's timeline (F-SES-21).
  const [onTimeline, setOnTimeline] = useState(false);
  const readAt = useStaffMessageRead(venue.id, awaiting);
  const formatter = useVenueFormatter();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const line = text.trim();
    if (line === '') {
      setError(t('floor.drawer.message.required'));
      return;
    }
    setError(undefined);
    const sent = () => {
      show({ tone: 'ok', title: t('floor.drawer.message.sent') });
      setText('');
      setNeedsAck(false);
    };
    if (session !== null) {
      toSession.mutate(
        { text: line, needsAck },
        {
          onSuccess: () => {
            sent();
            setAwaiting(null);
            setOnTimeline(true);
          },
          onError: (failure) => setError(problem.text(failure, { fallback: t('floor.drawer.message.failed') })),
        },
      );
      return;
    }
    message.mutate(
      { stationId: station.stationId, body: { text: line, needsAck } },
      {
        onSuccess: (command) => {
          const id = (command as { readonly id?: unknown } | undefined)?.id;
          sent();
          setAwaiting(needsAck && typeof id === 'string' ? id : null);
          setOnTimeline(false);
        },
        onError: (failure) => setError(problem.text(failure, { fallback: t('floor.drawer.message.failed') })),
      },
    );
  };

  return (
    <Frame plain={plain} title={t('floor.drawer.message.title')} description={t('floor.drawer.message.hint')} actions={actions}>
      <form className="console-form" aria-label={t('floor.drawer.message.title')} noValidate onSubmit={submit}>
        <TextField
          label={t('floor.drawer.message.label')}
          placeholder={t('floor.drawer.message.placeholder')}
          maxLength={MESSAGE_MAX}
          required
          value={text}
          onChange={(e) => setText(e.target.value)}
          error={error}
        />
        <div className="console-station-sheet__row">
          <Checkbox label={t('floor.drawer.message.needsAck')} checked={needsAck} onCheckedChange={(checked) => setNeedsAck(checked === true)} />
          <Button type="submit" variant="secondary" icon={<Icon name="message" />} busy={message.isPending || toSession.isPending}>
            {t('floor.drawer.message.send')}
          </Button>
        </div>
      </form>
      {awaiting !== null ? (
        <p className="console-muted" role="status">
          {readAt === null ? t('floor.drawer.message.awaitingRead') : t('floor.drawer.message.read', { time: formatter.time(readAt) })}
        </p>
      ) : null}
      {onTimeline ? (
        <p className="console-muted" role="status">
          {t('floor.drawer.message.onTimeline')}
        </p>
      ) : null}
      {!station.online ? <p className="console-muted">{t('floor.drawer.offlineHint')}</p> : null}
    </Frame>
  );
}

/** Maintenance for a while (F-STN-18); in maintenance already, End. `onStarted` closes a dialog it sits in. */
export function MaintenanceControls({ station, plain = false, actions, onStarted }: ActionFormProps & { readonly onStarted?: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const maintenance = useMaintenance(station);
  const [duration, setDuration] = useState<string>('30');

  return (
    <Frame plain={plain} title={t('floor.drawer.maintenance')} description={t('floor.drawer.maintenanceHint')} actions={actions}>
      {maintenance.inMaintenance ? (
        <div className="console-form__actions">
          <Button variant="secondary" icon={<Icon name="wrench" />} busy={maintenance.pending} onClick={() => maintenance.end(true, Number(duration))}>
            {t('floor.drawer.endMaintenance')}
          </Button>
        </div>
      ) : (
        <div className="console-station-sheet__row">
          <SegmentedControl
            size="sm"
            label={t('floor.drawer.duration')}
            value={duration}
            onValueChange={setDuration}
            options={MAINTENANCE_DURATIONS.map((minutes) => ({ value: minutes, label: t('floor.drawer.minutes', { n: Number(minutes) }) }))}
          />
          <Button variant="secondary" icon={<Icon name="wrench" />} busy={maintenance.pending} onClick={() => maintenance.start(Number(duration), true, onStarted)}>
            {t('floor.drawer.startMaintenance')}
          </Button>
        </div>
      )}
    </Frame>
  );
}

/** Restart or shut down, confirmed with its consequence first (spec/13 §2). */
export function PowerConfirm({
  station,
  action,
  plain = false,
  onDone,
}: {
  readonly station: FloorStationState;
  readonly action: 'restart' | 'shutdown';
  readonly plain?: boolean;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const power = useStationPower(venue.organizationId, venue.id);
  const notify = useCommandToasts();

  const send = () => {
    power.mutate({ stationId: station.stationId, body: { action } }, notify(t(`floor.power.${action}`)));
    onDone();
  };

  return (
    <Frame plain={plain} title={t('floor.drawer.power')}>
      <Notice
        tone="warn"
        role="alert"
        title={t(`floor.confirm.${action}`, { number: station.number })}
        action={
          <>
            <Button variant="ghost" size="sm" onClick={onDone}>
              {t('floor.confirm.cancel')}
            </Button>
            <Button variant="danger" className="clav-button--danger-solid" size="sm" busy={power.isPending} onClick={send}>
              {t(`floor.power.${action}`)}
            </Button>
          </>
        }
      />
      {!station.online ? <p className="console-muted">{t('floor.drawer.offlineHint')}</p> : null}
    </Frame>
  );
}

/**
 * Skip a PC's start-up setup (F-STN-26, ADR-0055): it opens for play now; the apps it has not installed yet install later,
 * while it is idle, and an install already running finishes first. It confirms first, in place, saying so (spec/13 §2),
 * then sends `POST …/stations/{s}/setup/skip`; the tile turns from Setting up once the PC reports it.
 */
export function SkipSetupConfirm({ station, onDone }: { readonly station: FloorStationState; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const venue = useCurrentVenue();
  const skip = useSkipStationSetup(venue.organizationId, venue.id);
  const setup = station.setup ?? null;
  const action = t('floor.drawer.skipSetup.sent');

  const send = () => {
    skip.mutate(
      { stationId: station.stationId, body: null },
      {
        onSuccess: () => show({ tone: 'ok', title: t('floor.drawer.sent', { action }) }),
        onError: (error: unknown) =>
          show({
            tone: 'danger',
            title: problemCode(error) === PROBLEM.conflict ? t('floor.drawer.skipSetup.notSettingUp', { number: station.number }) : problem.text(error, { fallback: t('floor.drawer.failed', { action }) }),
          }),
      },
    );
    onDone();
  };

  return (
    <Frame plain={false} title={t('floor.drawer.skipSetup.action')}>
      <Notice
        tone="info"
        title={t('floor.drawer.skipSetup.title', { number: station.number })}
        action={
          <>
            <Button variant="ghost" size="sm" onClick={onDone}>
              {t('floor.drawer.skipSetup.cancel')}
            </Button>
            <Button variant="primary" size="sm" busy={skip.isPending} onClick={send}>
              {t('floor.drawer.skipSetup.send')}
            </Button>
          </>
        }
      >
        {setup === null ? null : <p>{t('floor.drawer.skipSetup.progress', { done: setup.done, total: setup.total })}</p>}
        <p>{t('floor.drawer.skipSetup.body')}</p>
      </Notice>
    </Frame>
  );
}

/**
 * Mint an offline code (F-SES-24, spec/05 §8.4): when the venue has lost its connection, staff read
 * the 8 digits out to the player, who types them at the station to start, extend or unlock it for
 * maintenance. The code is shown large with when it stops working; changing the pick clears it.
 */
export function OfflineCodeControls({ station, plain = false, actions }: ActionFormProps): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const venue = useCurrentVenue();
  const generate = useGenerateOfflineCode(venue.organizationId, venue.id);
  const [action, setAction] = useState<OfflineCodeAction>('start');
  const [minutes, setMinutes] = useState<string>('60');
  const [code, setCode] = useState<OfflineCode | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const mint = () => {
    setError(undefined);
    setCode(null);
    generate.mutate(
      { stationId: station.stationId, body: { action, minutesBucket: Number(minutes) as OfflineCode['minutesBucket'] } },
      {
        onSuccess: (minted) => setCode(minted),
        onError: (failure) =>
          setError(problemCode(failure) === PROBLEM.conflict ? t('floor.drawer.offlineCode.noSecret') : problem.text(failure, { fallback: t('floor.drawer.offlineCode.failed') })),
      },
    );
  };

  const copy = (value: string) => {
    void navigator.clipboard?.writeText(value).then(
      () => setCopied(true),
      () => undefined,
    );
  };

  return (
    <Frame plain={plain} title={t('floor.drawer.offlineCode.title')} description={t('floor.drawer.offlineCode.hint')} actions={actions}>
      <div className="console-station-sheet__row">
        <SegmentedControl
          size="sm"
          label={t('floor.drawer.offlineCode.action')}
          value={action}
          onValueChange={(value) => {
            setAction(value as OfflineCodeAction);
            setCode(null);
          }}
          options={OFFLINE_CODE_ACTIONS.map((value) => ({ value, label: t(`floor.drawer.offlineCode.actions.${value}`) }))}
        />
        <SelectField
          label={t('floor.drawer.offlineCode.minutes')}
          value={minutes}
          onValueChange={(value) => {
            setMinutes(value);
            setCode(null);
          }}
          options={OFFLINE_CODE_MINUTE_BUCKETS.map((bucket) => ({ value: String(bucket), label: t('floor.drawer.minutes', { n: bucket }) }))}
        />
      </div>
      <div className="console-form__actions">
        <Button variant="secondary" icon={<Icon name="lock" />} busy={generate.isPending} onClick={mint}>
          {t('floor.drawer.offlineCode.generate')}
        </Button>
      </div>
      {error === undefined ? null : <Notice tone="danger" role="alert" title={error} />}
      {code === null ? null : (
        <div className="console-station-sheet__code">
          <span className="console-station-sheet__code-digits console-figure" aria-label={t('floor.drawer.offlineCode.codeLabel', { code: code.code.split('').join(' ') })}>
            {`${code.code.slice(0, 4)} ${code.code.slice(4)}`}
          </span>
          <span className="console-muted">
            {t(`floor.drawer.offlineCode.carries.${code.action}`, { n: code.minutesBucket })}
            {' · '}
            {t('floor.drawer.offlineCode.expires', { time: new Date(code.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}
          </span>
          <div className="console-station-sheet__row">
            <Button variant="ghost" size="sm" onClick={() => copy(code.code)}>
              {t('floor.drawer.offlineCode.copy')}
            </Button>
            <span role="status" className="console-muted">
              {copied ? t('floor.drawer.offlineCode.copied') : ''}
            </span>
          </div>
        </div>
      )}
    </Frame>
  );
}
